import dayjs from "dayjs";
import { addAncillaryToOffer, refundAncillaryFromOffer } from "../../../../app/api/getOffers";
import { getUserDetails } from "../../../../app/api/getUserDetails";
import { patchPassenger } from "../../../../app/api/patchPassenger";
import { patchPurchaser } from "../../../../app/api/patchPurchaser";
import { updateCustomerById } from "../../../../app/api/updateCustomerById";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { determinePassengerType, determineIsDOBRequiredForPassengerType } from "../../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IDeterminePassenger, IUserResponse } from "../../../../Orchestrator/Types/Index";
import { IAddAncillaryToOfferRequest, IAddonsPassengerLeg, IAlreadyAddonsForPassenger } from "../../../../Orchestrator/Types/Interface/IAddOns";
import { ICustomerType } from "../../../../Orchestrator/Types/Interface/ICustomer";
import { IPaymentProcessActionParams } from "../../../../Orchestrator/Types/Interface/IPaymentProcess";
import {
  IPatchPassengerRequest,
  IPurchaserRequest,
} from "../../../../Orchestrator/Types/Interface/IPurchaserRequest";

export class PaymentProcessAction extends BaseAction<string, IPaymentProcessActionParams> {
  // Coalesce concurrent invocations: a quick double-click on Pay would
  // otherwise re-fire the refund-offers POST against a booking that has
  // already been modified, producing a 400 BOOKING_MODIFICATION_NOT_ALLOWED.
  protected override deduplicate = true;

  async calculateAge(dateOfBirth: Date) {
    const dob = dayjs(dateOfBirth);
    const today = dayjs();
    const age = today.diff(dob, "year");
    return age;
  }

  async fetchUserData(): Promise<IUserResponse> {
    try {
      const isAnonymous: boolean = getRecoilStateValue("isAnonymousStateAtom") as boolean;
      if (isAnonymous && isAnonymous === true) {
        return {} as IUserResponse;
      }
      const response: IApiResponse<IUserResponse> = await getUserDetails(false);
      if (response.status === "ok" && response.answer) {
        const userData: IUserResponse = response.answer;

        const updatedUserData: IUserResponse = {
          ...userData,
          firstName: userData.firstName === "-" ? "" : userData.firstName,
          lastName: userData.lastName === "-" ? "" : userData.lastName,
        };

        updateRecoilState<IUserResponse>("userDataAtom", updatedUserData);

        return response.answer;
      } else {
        console.warn("Failed to fetch user data:", response);
        throw new Error("Failed to fetch user data");
      }
    } catch (error: any) {
      console.error("Error fetching user data:", error);
      throw new Error("Failed to fetch user data");
    }
  }

  private getDefault<T>(value: T | undefined, defaultValue: T): T {
    return value !== undefined ? value : defaultValue;
  }

  private isSameInvoicingAddress(
    existing: any,
    next: any
  ): boolean {
    if (!existing && !next) return true;
    if (!existing || !next) return false;
    const norm = (v: any) => (v == null ? "" : String(v));
    return (
      norm(existing.name) === norm(next.name) &&
      norm(existing.trn) === norm(next.trn) &&
      norm(existing.street) === norm(next.street) &&
      norm(existing.complement) === norm(next.complement) &&
      norm(existing.city) === norm(next.city) &&
      norm(existing.countryCode) === norm(next.countryCode)
    );
  }

  private async updateCustomerHelper(customerId: string, customerData: Partial<any>) {
    try {
      const response: any = await updateCustomerById(customerId, customerData);
      if (response.status === "ok" && response.answer) {
        const updatedCustomer: IUserResponse = response.answer;
        // PUT response carries the full updated customer — use it as the
        // new userDataAtom value instead of round-tripping a second /me.
        const normalized: IUserResponse = {
          ...updatedCustomer,
          firstName: updatedCustomer.firstName === "-" ? "" : updatedCustomer.firstName,
          lastName: updatedCustomer.lastName === "-" ? "" : updatedCustomer.lastName,
        };
        updateRecoilState<IUserResponse>("userDataAtom", normalized);
      } else {
        throw new Error("Failed to update customer.");
      }
    } catch (error: any) {
      console.error("Error updating customer:", error);
    }
  }

  async updateBasicInfo(
    customerId: string,
    basicInfo: {
      email?: string;
      firstName?: string;
      lastName?: string;
      birthdate?: string;
      phoneNumber?: string;
      gender?: "MALE" | "FEMALE";
      countryCode?: string;
      preferredLanguage?: string;
      title?: string;
    },
    invoicingAddress?: {
      name?: string;
      trn?: string;
      street?: string;
      complement?: string;
      city?: string;
      countryCode?: string;
    }
  ) {
    try {
      const validBasicInfo = {
        email: this.getDefault(basicInfo?.email, ""),
        firstName: this.getDefault(basicInfo?.firstName, ""),
        lastName: this.getDefault(basicInfo?.lastName, ""),
        birthdate: this.getDefault(basicInfo?.birthdate, undefined),
        phoneNumber: this.getDefault(basicInfo?.phoneNumber, ""),
        gender: this.getDefault(basicInfo?.gender, undefined),
        countryCode: this.getDefault(basicInfo?.countryCode, undefined),
        preferredLanguage: this.getDefault(basicInfo?.preferredLanguage, ""),
        title: this.getDefault(basicInfo?.title, undefined),
      };

      // Combine basic info with invoice address if provided
      const updateData: any = { ...validBasicInfo };
      if (invoicingAddress) {
        updateData.invoicingAddress = invoicingAddress;
      }

      // Call the general customer update helper
      await this.updateCustomerHelper(customerId, updateData);
    } catch (error: any) {
      console.error("Error updating basic customer information:", error);
    }
  }

  async updatePassengerList(customerId: string, passengers: Partial<ICustomerType['additionalPassengers']>) {
    const validPassengers = passengers?.map(passenger => {
      const passengerData: any = {
        firstName: this.getDefault(passenger?.firstName, 'Unknown'),
        lastName: this.getDefault(passenger?.lastName, 'Unknown')
      };
      if (passenger?.passengerType) {
        passengerData.passengerType = passenger.passengerType
      }
      if (passenger?.title) {
        passengerData.title = passenger.title
      }

      if (passenger?.birthdate) {
        passengerData.birthdate = passenger.birthdate?.split('T')[0];
      }

      return passengerData;
    }) ?? [];
    await this.updateCustomerHelper(customerId, { additionalPassengers: validPassengers });
  }

  override async run(): Promise<string> {
    let returnString: any = "success";
    try {
      const { passengers, booking, bookingId, customer, updatePersonalInfo, passengerTypes, invoiceDetails, manageAddonsNewConfirmedAddons, manageAddonsRemovedAddons, carLicensePlate } = this.params!;
      updateRecoilState("paymentSuccessModalAtom", false);
      updateRecoilState("selectedTripsDataAtom", null);
      let confirmAddonsList: IAddonsPassengerLeg[] = manageAddonsNewConfirmedAddons || [] as IAddonsPassengerLeg[]
      let removedAddonsList: IAlreadyAddonsForPassenger[] = manageAddonsRemovedAddons || [] as IAlreadyAddonsForPassenger[]
      const isManageSeating = getRecoilStateValue("isManageSeatingAtom") || false
      const patchAddonsOnConfirm =
        (getRecoilStateValue("patchAddonsOnConfirmAtom") as boolean) ?? true;
      // Reuse user data already fetched on page load (PassengerDetails /
      // topbar / GetUserDetailsAction populate userDataAtom). Fall back to
      // /me only if the atom is empty — e.g. anonymous flow that just
      // upgraded to authenticated, or a hard reload that skipped earlier
      // init steps.
      const isAnonymous: boolean = (getRecoilStateValue("isAnonymousStateAtom") as boolean) || false;
      let userData: any = !isAnonymous
        ? (getRecoilStateValue<IUserResponse>("userDataAtom") || null)
        : null;
      if (!isAnonymous && (!userData || !userData.id)) {
        userData = await this.fetchUserData();
      }

      // Get passengers that should be remembered
      const rememberedPassengers = passengers.filter(p => p.remember);

      // Convert remembered passengers to AdditionalPassenger format      
      const newAdditionalPassengers = rememberedPassengers.map(passenger => {
        const determinePassenger: IDeterminePassenger = {
          age: passenger?.age,
          cards: passenger?.cards || [],
          prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
        }
        const passengerType = determinePassengerType(determinePassenger);
        const rememberPassType = passengerTypes.find(d => d.id === passengerType)?.value
        return {
          firstName: passenger.detail.firstName,
          lastName: passenger.detail.lastName,
          birthdate: determineIsDOBRequiredForPassengerType(passengerType) ? passenger.dateOfBirth?.toISOString() : null,
          ...rememberPassType ? { passengerType: rememberPassType } : {}
        }
      });

      // Save updated passenger list only if there are new passengers to add      
      if (newAdditionalPassengers.length > 0) {

        const existingPassengers = userData?.additionalPassengers || [];

        const newUniquePassengers = newAdditionalPassengers.filter(
          newPassenger =>
            !existingPassengers.some((existingPassenger: { firstName: string; lastName: string; birthdate: any; passengerType: any; }) =>
              existingPassenger.firstName.trim().toLowerCase() === newPassenger.firstName.trim().toLowerCase() &&
              existingPassenger.lastName.trim().toLowerCase() === newPassenger.lastName.trim().toLowerCase() &&
              (existingPassenger.birthdate ?? "") === (newPassenger.birthdate ?? "") &&
              (existingPassenger.passengerType ?? "") === (newPassenger.passengerType ?? "")
            )
        );

        if (newUniquePassengers.length > 0) {
          const updatedPassengerList = [...(userData?.additionalPassengers || []), ...newAdditionalPassengers];
          await this.updatePassengerList(userData.id.toString(), updatedPassengerList);
      }
    }

      // Patch passenger information
      for (const passenger of passengers) {
        const patchRequest: IPatchPassengerRequest = {
          id: passenger.id,
          summary: `${passenger.detail?.firstName || ""} ${passenger.detail?.lastName || ""}`,
          externalRef: passenger.externalRef,
          type: passenger.type,
          detail: {
            preferredLanguage: "en",
            summary: `${passenger.detail.firstName} ${passenger.detail.lastName}`,
            firstName: passenger.detail.firstName,
            lastName: passenger.detail.lastName,
            email: passenger.detail.email || customer.email,
            phoneNumber: passenger.detail.phoneNumber || customer.phoneNumber,
          },
          // age: age, // We can not patch age, because if prebook call with 16 and on checkout if age is calculating other than 16 then after a sales API get fail
          dateOfBirth: passenger?.dateOfBirth
            ? passenger.dateOfBirth instanceof Date
              ? passenger.dateOfBirth.toISOString()
              : new Date(passenger.dateOfBirth).toISOString()
            : "",
        };
        await patchPassenger(patchRequest, bookingId, false);
      }

      if (customer) {
        const purchaserRequest: IPurchaserRequest = {
          externalRef: passengers[0]?.externalRef || "",
          detail: {
            summary: "",
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            phoneNumber: customer.phoneNumber,
          },
        };
        try {
          await patchPurchaser(bookingId, purchaserRequest, false);
        } catch {
          // Handle error
        }
      }

      /**
       * Removed ancillary(Addons) from the booked offer
       * First prepare all the ancillary(addons) Is's for all trip
       * Second, refund that all the ancillary.
       * Third, patch the offer into booking.
      */
      if (!patchAddonsOnConfirm && removedAddonsList.length > 0) {
        const allAncillaryFulfillmentIds = removedAddonsList.map(d => d?.ancillaryId || "").filter(d => d != "")
        const request = await refundAncillaryFromOffer(allAncillaryFulfillmentIds, bookingId)
        if (request.status === "ok" && request.code === 200) {
          if (isManageSeating) {
            sessionStorage.setItem("needToShowRemovedAddons", "yes")
          } else {
            sessionStorage.removeItem("needToShowRemovedAddons")
          }
          const refundOffers = request.answer?.refundOffers || []
          // if (refundOffers.length > 0) {
          //   const allRefundOffersIds = refundOffers.map(d => d.id)
          //   if (allRefundOffersIds.length > 0) {
          //     for (let i = 0; i < allRefundOffersIds.length; i++) {
          //       const refundOfferId = allRefundOffersIds[i];
          //       //await patchRefundOffer(refundOfferId, bookingId)
          //     }
          //     if (isManageSeating) {
          //       sessionStorage.setItem("needToShowRemovedAddons", "yes")
          //     } else {
          //       sessionStorage.removeItem("needToShowRemovedAddons")
          //     }
          //   }
          // }
        }
      }

      /**
       * Added ancillary(Addons) to booked offer
       * First prepare all the addons passenger based on ancillary for all trip
       * Second, prepare all the ancillary for all the offer
       * Third, prepare payload(API) to add ancillary to offer
      */
      if (!patchAddonsOnConfirm && (confirmAddonsList || []).length > 0) {
        const allTripLegPassengerAddons: IAddAncillaryToOfferRequest[] = []
        confirmAddonsList?.forEach(addonsDetail => {
          addonsDetail.addonsPassenger.forEach(addonsPass => {
            Object.values(addonsPass?.addonItem || {}).forEach(async (addonsItem: any) => {
              const tripLegPassengerAddons: IAddAncillaryToOfferRequest = {
                offerId: addonsItem?.additionalOfferId || "",
                bookedOfferId: addonsItem?.bookedOfferId || "",
                ancillaryOfferId: addonsItem?.ancillaryOfferId || "",
                passengerRefs: [addonsPass.passengerRef],
                tripCoverage: {
                  coveredTripId: addonsItem?.tripCoverageId || ""
                }
              };
              allTripLegPassengerAddons.push(tripLegPassengerAddons)
            })
          })
        })
        if (allTripLegPassengerAddons.length > 0) {
          const mergeOffers = allTripLegPassengerAddons.reduce((acc: any, item: IAddAncillaryToOfferRequest) => {
            const key = `${item.offerId}-${item.ancillaryOfferId}-${item.tripCoverage.coveredTripId}`;
            acc[key] = acc[key] || { ...item, passengerRefs: [] };
            acc[key].passengerRefs.push(...item.passengerRefs);
            return acc;
          }, {})

          const mergeDataOfferWise = (Object.values(mergeOffers) as IAddAncillaryToOfferRequest[]).reduce((acc: any, item: IAddAncillaryToOfferRequest) => {
            const key = `${item?.bookedOfferId}`;
            acc[key] = [
              ...acc[key] || [],
              item
            ]
            return acc;
          }, {})

          const allKeys = Object.keys(mergeDataOfferWise)
          const addonsPromises = [];
          for (let i = 0; i < allKeys.length; i++) {
            const offerId = allKeys[i]
            const offerAddons = mergeDataOfferWise[allKeys[i]]
            for (let j = 0; j < offerAddons.length; j++) {
              delete offerAddons[i]?.bookedOfferId
              const request = await addAncillaryToOffer(offerAddons[j], bookingId, offerId)
              addonsPromises.push(request)
            }
          }
          if (addonsPromises.length > 0) {
            await Promise.all(addonsPromises)
            if (isManageSeating) {
              sessionStorage.setItem("needToShowAddedAddons", "yes")
            } else {
              sessionStorage.removeItem("needToShowAddedAddons")
            }
          }
        }
      }

      if (invoiceDetails && Object.values(invoiceDetails).some(value => value && value.trim())) {
        const invoiceRequestPayload = {
          formData: {
            companyName: invoiceDetails.companyName || "",
            trn: invoiceDetails.trn || "",
            address1: invoiceDetails.address1 || "",
            address2: invoiceDetails.address2 || "",
            city: invoiceDetails.city || "",
            countryBilling: invoiceDetails.countryBilling || ""
          },
          userEmail: customer.email || ""
        };
        localStorage.setItem('invoiceRequestPayload', JSON.stringify(invoiceRequestPayload));
      }

      // check if it's pass booking
      let consumptionType = booking.booking?.bookedOffers?.[0]?.admissions?.[0]?.indicatedConsumption?.unit?.objectType
      const isPassBooking = ["DayTravelAccountUnit", "TripTravelAccountUnit"].includes(consumptionType || "");
      if (isPassBooking) {
        localStorage.setItem("isPassBooking", "true");
      }

      // Prepare invoice address if provided
      let invoicingAddress: any = undefined;
      if (invoiceDetails && userData.id && Object.values(invoiceDetails).some(value => value && value.trim())) {
        invoicingAddress = {
          name: invoiceDetails.companyName,
          trn: invoiceDetails.trn,
          street: invoiceDetails.address1,
          complement: invoiceDetails.address2,
          city: invoiceDetails.city,
          countryCode: invoiceDetails.countryBilling
        };
      }

      if (updatePersonalInfo) {
        // Combine basic info update with invoice update to reduce API calls
        await this.updateBasicInfo(userData.id.toString(), {
          firstName: customer.firstName,
          lastName: customer.lastName,
          phoneNumber: customer.phoneNumber,
          email: userData.email,
          ...(carLicensePlate ? { carLicensePlate } : ""),
        }, invoicingAddress);
      } else if (invoicingAddress) {
        // Only PUT when something actually changed — invoice details persist
        // across sessions (localStorage), so the form usually re-submits the
        // exact same address that's already on the server.
        const existingPlate = userData?.carLicensePlate || "";
        const plateChanged = !!carLicensePlate && carLicensePlate !== existingPlate;
        const invoiceChanged = !this.isSameInvoicingAddress(
          userData?.invoicingAddress,
          invoicingAddress
        );
        if (invoiceChanged || plateChanged) {
          await this.updateCustomerHelper(userData.id.toString(), {
            ...(invoiceChanged ? { invoicingAddress } : {}),
            ...(plateChanged ? { carLicensePlate } : {}),
          });
        }
      } else if (carLicensePlate) {
        const existingPlate = userData?.carLicensePlate || "";
        if (carLicensePlate !== existingPlate) {
          await this.updateCustomerHelper(userData.id.toString(), {
            carLicensePlate,
          });
        }
      }

      // Refunds for deselected baseline extras are now settled by the
      // backend as part of POST /api/payment/session — the frontend sends
      // the modified ancillaries (added + queued-for-refund) in the body
      // and the backend orchestrates both the new charge and the refund
      // server-side. See buildSessionAncillaries / RedirectToPaymentGateway.

    } catch (error) {
      console.error("Error during payment type change:", error);
      returnString = "fail"
    } finally {
      updateRecoilState("addonFlowStartedAtom", false);
      return returnString
    }
  }
}

export const paymentProcessActionInstance = new PaymentProcessAction();
